// src/pages/affiliate/LoginPage.tsx
import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useUI } from '../../context/UIContext';
import { getBaseUrl } from '../../config/api';
import '../../styles/AffiliateLogin.css';

const AffiliateLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar, showLoading, hideLoading } = useUI();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      showLoading('Signing in...');

      const response = await fetch(`${getBaseUrl()}/v1/affiliate/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      console.log('Login response:', { status: response.status, data });

      hideLoading();

      if (response.ok && data.success) {
        // Store token (backend returns accessToken directly, not nested in data)
        const token = data.accessToken || data.data?.token || data.data?.accessToken;
        const affiliate = data.affiliate || data.data?.affiliate;
        
        if (!token) {
          console.error('No token in response:', data);
          showSnackbar('Login failed: No token received', 'error');
          return;
        }
        
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem('affiliateToken', token);
        if (data.refreshToken) {
          storage.setItem('affiliateRefreshToken', data.refreshToken);
        }
        if (affiliate) {
          storage.setItem('affiliateUser', JSON.stringify(affiliate));
          if (affiliate.status) {
            sessionStorage.setItem('affiliateStatus', affiliate.status);
          }
        }

        console.log('Token stored, redirecting to dashboard...');
        showSnackbar('Login successful!', 'success');
        
        // Redirect to dashboard or intended page
        const from = (location.state as any)?.from || '/affiliate/dashboard';
        setTimeout(() => {
          navigate(from, { replace: true });
        }, 100);
      } else {
        console.error('Login failed:', data);
        showSnackbar(data.message || data.error || 'Login failed', 'error');
      }
    } catch (error) {
      hideLoading();
      console.error('Login error:', error);
      showSnackbar('An error occurred during login', 'error');
    }
  };

  return (
    <div className="affiliate-login">
      <div className="affiliate-login__container">
        <div className="affiliate-login__header">
          <h1>Referral Agent Login</h1>
          <p>Welcome back! Sign in to your referral agent account</p>
        </div>

        {(location.state as any)?.registered && (
          <div className="affiliate-login__alert affiliate-login__alert--success">
            <i className="fas fa-check-circle"></i>
            <div>
              <strong>Registration successful!</strong>
              <p>Please check your email to verify your account before logging in.</p>
            </div>
          </div>
        )}

        {(location.state as any)?.verified && (
          <div className="affiliate-login__alert affiliate-login__alert--success">
            <i className="fas fa-check-circle"></i>
            <div>
              <strong>Email verified!</strong>
              <p>Your account is now active. You can log in below.</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="affiliate-login__form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-wrapper">
              <i className="fas fa-envelope"></i>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="your@email.com"
                className={errors.email ? 'error' : ''}
              />
            </div>
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <i className="fas fa-lock"></i>
              <input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => updateField('password', e.target.value)}
                placeholder="••••••••"
                className={errors.password ? 'error' : ''}
              />
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="affiliate-login__options">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>Remember me</span>
            </label>
            <Link to="/affiliate/forgot-password" className="link-text">
              Forgot password?
            </Link>
          </div>

          <button type="submit" className="btn-login">
            Sign In
          </button>
        </form>

        <div className="affiliate-login__divider">
          <span>or</span>
        </div>

        <div className="affiliate-login__footer">
          <p>
            Don't have an account?{' '}
            <Link to="/affiliate/register" className="link-text link-text--bold">
              Join the referral program
            </Link>
          </p>
          <p>
            <Link to="/" className="link-text">
              ← Back to main site
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AffiliateLoginPage;

